<template>
  <div class="report-access-selection-controls">
    <div
      :class="{
        'pb-16': !showUsers || !hideControls,
      }"
      class="flex text-neutral-text-strong font-bold text-lg"
    >
      <BittsButton
        v-if="!showUsers"
        data-testid="user-perm-back-button"
        :center-icon="['fak', 'arrow-left']"
        variant="ghost"
        class="mr-8"
        size="small"
        @click="onClearSelected"
      />
      <span v-if="!hideControls">Share with Team Members</span>
    </div>
    <BittsMultiselect
      v-model="selectedTeamMemberOptions"
      custom-filter="label"
      placeholder="Type names or emails"
      mode="multiple"
      :options="availableTeamMemberOptions"
      :show-checkboxes="false"
      :show-selected-options="false"
      :dropdown-class="
        !!selectedTeamMemberOptions.length
          ? 'c-report-access-module__hide-dropdown'
          : ''
      "
    >
      <template #emptyStateContent>
        <p class="flex justify-center text-neutral-text-weak pb-12">
          {{ emptyStateMessage }}
        </p>
        <BittsButton
          text="Report Access"
          size="x-small"
          class="w-full"
          type="neutral"
          :left-icon="['fak', 'add']"
          @click="onInviteTeamBtnClicked"
        />
      </template>
    </BittsMultiselect>
    <BittsDivider class="my-16" />
    <div v-if="showUsers" class="overflow-auto max-h-[375px]">
      <p class="text-neutral-text-strong pb-8 font-bold"> Report Access </p>
      <ReportUserAccessCard
        v-for="user in existingUsers"
        :key="user.user_id"
        :user="user"
        :is-me="user.user_id === currentUser?.id"
        :is-creator="reportCreatorId === currentUser?.id"
        :loading="loadingState[user.user_id]"
        :editable="canAccessBeEdited(user)"
        @option-selected="onClickedRemoveUser"
      />
    </div>
    <div v-else-if="!hideControls" class="flex justify-end">
      <BittsButton
        text="Cancel"
        class="mr-16"
        type="neutral"
        :loading="isLoading"
        @click="onClearSelected"
      />
      <BittsButton
        text="Send Invites"
        :loading="isLoading"
        @click="onClickedSendInvite"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { BittsButton, BittsDivider, BittsMultiselect } from '@crossbeam/bitts';
import { AddSelectProperties, Nullable } from '@crossbeam/types';

import { keyBy, sortBy } from 'lodash';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import ReportUserAccessCard, {
  Option,
} from '@/components/reports/ReportUserAccessCard.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { useTeamStore } from '@/stores';
import {
  ReportTeamAccessAuthorization,
  ReportTeamAccessAuthorizationUser,
  UserWithReportAccess,
} from '@/types/report_team_access';

const {
  existingUsers = [],
  reportId = '',
  reportCreatorId = null,
  hideControls = false,
} = defineProps<{
  existingUsers: UserWithReportAccess[];
  reportId: string;
  reportCreatorId: Nullable<number>;
  hideControls?: boolean;
}>();

// const emit = defineEmits<(e: 'invite-button-clicked' | 'closed') => void>();
const teamStore = useTeamStore();
const selectedTeamMemberOptions = ref<string[]>([]);
const isLoading = ref(false);

type LoadingState = Record<string, boolean>;
const loadingState = ref<LoadingState>({});

const { currentUser } = useAuth();
const router = useRouter();

const teamMembers = ref<ReportTeamAccessAuthorization[]>([]);
async function getTeamMembers() {
  const { data, error } = await crossbeamApi.GET(
    '/v0.1/reports/{id}/team-access',
    { params: { path: { id: reportId } } },
  );
  if (error) throw error;

  teamMembers.value = data?.authorizations || [];
}

type UserForSelect = AddSelectProperties<ReportTeamAccessAuthorizationUser>;
const availableTeamMemberOptions = computed(() => {
  const unsortedUsers: UserForSelect[] = [];
  const existingUsersByUserId = keyBy(existingUsers, 'user_id');
  teamMembers.value.forEach((auth) => {
    const { user } = auth;
    if (!user) return;
    const foundUser = existingUsersByUserId[user.id.toString()];
    if (!foundUser) {
      unsortedUsers.push({
        ...user,
        value: `${user.id}__${user.first_name}__${user.last_name}__${user.email}`,
        label: `${user.first_name} ${user.last_name}`,
      });
    }
  });
  return sortBy(unsortedUsers, ['label']);
});

function canAccessBeEdited(_user: UserWithReportAccess) {
  // TODO: Will available in a forthcoming story
  return false;
}

const showUsers = computed(() => !selectedTeamMemberOptions.value.length);

function onClearSelected() {
  selectedTeamMemberOptions.value = [];
  loadingState.value = {};
}

function onClickedSendInvite() {
  return null;
}

function onClickedRemoveUser(_option: Option) {
  return null;
}

const multipleTeamMembersInOrg = computed(
  () =>
    !availableTeamMemberOptions.value.length && teamStore.coreUsers.length > 1,
);

const emptyStateMessage = computed(() => {
  if (multipleTeamMembersInOrg.value) {
    return 'Looks like your whole team has been invited to collaborate';
  }
  return 'To collaborate with more teammates on this Report, add more members to your organization';
});

async function onInviteTeamBtnClicked() {
  await router.push({ name: 'invite_user' });
}

onMounted(getTeamMembers);
</script>
<style lang="pcss" scoped>
.report-access-selection-controls {
  @apply p-16 w-[480px] max-h-[600px];
}
</style>
